import { msalAuthProvider } from "ra-auth-msal";
import { PublicClientApplication, Configuration, AccountInfo, BrowserAuthError } from "@azure/msal-browser";
import { AuthProvider } from "react-admin";
import { getConfig } from "../../config/app.config";
import { storeReturnUrl } from '../return-url.utils';


export class AzureAuthProvider {
  private msalConfig: Configuration;
  private clientApplication: PublicClientApplication;

  constructor() {
    this.msalConfig = {
      auth: {
        clientId: getConfig('VITE_MSAL_CLIENT_ID'),
        authority: `${getConfig('VITE_MSAL_AUTHORITY')}/${
          getConfig('VITE_MSAL_TENANT_ID')
        }`,
        redirectUri: getConfig('VITE_MSAL_CALLBACK'),
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: false,
        // Add cache key prefix to prevent conflicts with other apps using same auth provider
        // This ensures proto-admin-portal cache doesn't conflict with proto-client or other apps
        claimsBasedCachingEnabled: true,
      },
      system: {
        // Add unique logger callback name to avoid conflicts
        loggerOptions: {
          loggerCallback: (level, message, containsPii) => {
            if (containsPii) return;
            switch (level) {
              case 0: // LogLevel.Error
                console.error(`[MSAL-AdminPortal] ${message}`);
                return;
              case 1: // LogLevel.Warning
                console.warn(`[MSAL-AdminPortal] ${message}`);
                return;
              case 2: // LogLevel.Info
                console.info(`[MSAL-AdminPortal] ${message}`);
                return;
              case 3: // LogLevel.Verbose
                console.debug(`[MSAL-AdminPortal] ${message}`);
                return;
            }
          }
        }
      }
    };
    this.clientApplication = new PublicClientApplication(this.msalConfig);
  }

  private async handleRedirect(): Promise<void> {
    try {
      const response = await this.clientApplication.handleRedirectPromise();
      if (response) {
        console.log("[MSAL] Redirect login complete:", response);

        // Set active account so getPermissionsFromAccount receives the account
        this.clientApplication.setActiveAccount(response.account);        
      } else {
        console.log("[MSAL] No redirect response to handle");
      }
    } catch (error) {
      console.error("[MSAL] Error handling redirect:", error);
      // Log error but don't interfere with auth flow
    }
  }
  
  public getClientApplication() {
    return this.clientApplication;
  }

  public async getAuthProvider(): Promise<AuthProvider> {
    await this.clientApplication.initialize();
    await this.handleRedirect(); // <-- handle the redirect before authProvider is created
    
    // If no accounts exist after handling redirect, trigger login
    const accounts = this.clientApplication.getAllAccounts();
    if (accounts.length === 0) {
      console.log('[MSAL] No accounts found after initialization - triggering login redirect');
      await this.clientApplication.loginRedirect({
        scopes: ["openid", "profile", "User.Read", "offline_access"],
      });
      // This will redirect away - throw to prevent further execution
      throw new Error('Redirecting to Azure AD for login');
    }

    const baseAuthProvider = msalAuthProvider({
      msalInstance: this.clientApplication,
      redirectOnCheckAuth: false, // Don't redirect to /login - we'll handle auth ourselves
      getPermissionsFromAccount: async (account:AccountInfo) => {
        console.log('getPermissionsFromAccount');
        console.log('got account', account);
      
        console.log('idTokenClaims', account?.idTokenClaims);
        console.log('roles', account?.idTokenClaims?.roles);
        
        return account?.idTokenClaims?.roles || [];
      }
    });
    
    // Wrap both checkAuth and logout with custom logic
    return {
      ...baseAuthProvider,
      checkAuth: async (params: any) => {
        const accounts = this.clientApplication.getAllAccounts();
        console.log('accounts', accounts);
        console.log('accounts[0]', accounts[0]);
      
        
        if (accounts.length === 0) {
          // No accounts - just reject, don't redirect
          // loginPage={false} in Admin prevents redirect loop
          return Promise.reject(new Error('No authenticated session'));
        }
        
        // Ensure active account is set (safeguard for page refresh)
        if (!this.clientApplication.getActiveAccount() && accounts.length > 0) {
          this.clientApplication.setActiveAccount(accounts[0]);
        }
        
        // If accounts exist, use the base provider's checkAuth
        return baseAuthProvider.checkAuth(params);
      },
      logout: async () => {
        console.log("[MSAL] Custom logout: Resetting loadingState");
        
        // Store current URL before logout for deep linking on next login
        storeReturnUrl();
        
        // Reset loadingState to force environment selection on next login
        sessionStorage.setItem('loadingState', 'preEnvSelected');
        //sessionStorage.clear();
        // Then call the original logout
        return baseAuthProvider.logout();
      }
    };
  }
}
